import { Box, Button, ButtonRow, Card, FormGroup, Select } from '@ksyos/design-system';
import { Koi } from '@ksyos/koi';
import { useState } from 'react';
import { DatasetType } from '@common/contexts/support/domain/enums/datasetType';
import { ExporterType } from '@common/contexts/support/domain/enums/exporterType';
import { useFormData } from '@frontend/contexts/support/application/hooks/useFormData';
import { Exporter } from '@frontend/contexts/support/application/utils/exporter';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

type FormData = {
    datasetType?: DatasetType;
};

export function AdminProcessesOpen() {
    const language = getSelectedLanguage();

    const formDataSchema = Koi.object({
        datasetType: Koi.koi().enum(DatasetType),
    });

    const { formData, update, errors, validate } = useFormData<FormData>(formDataSchema);

    const [isExporting, setIsExporting] = useState(false);

    const selectOptions = [
        {
            label: language.adminProcesses.exportType.holterEcgExamination.groupName,
            options: [
                {
                    label: language.adminProcesses.exportType.holterEcgExamination.holterRead,
                    value: DatasetType.AssetNeededOrUploadingTcoh,
                },
                {
                    label: language.adminProcesses.exportType.holterEcgExamination.holterAtAnalyst,
                    value: DatasetType.AnalysisTcoh,
                },
                {
                    label: language.adminProcesses.exportType.holterEcgExamination.holterAtGrader,
                    value: DatasetType.GraderTcoh,
                },
            ],
        },
        {
            label: language.adminProcesses.exportType.consultations.groupName,
            options: [
                {
                    label: language.adminProcesses.exportType.consultations.consultationsAtSpecialist,
                    value: DatasetType.RoundOneConsults,
                },
                {
                    label: language.adminProcesses.exportType.consultations.consultationsSecondRoundAtSpecialist,
                    value: DatasetType.RoundTwoConsults,
                },
            ],
        },
    ];

    return (
        <Box marginTop={6}>
            <Card>
                <Card.Body>
                    <FormGroup
                        hasErrors={errors.has('datasetType')}
                        isRequired={true}
                        label={language.adminProcesses.processStep}
                    >
                        <Select
                            error={getValidationErrorTranslation(errors.get('datasetType'))}
                            onChange={(value) => update({ datasetType: value?.value })}
                            options={selectOptions}
                            value={getDatasetTypeOption(formData.datasetType)}
                        />
                    </FormGroup>
                    <ButtonRow>
                        <Button
                            isLoading={isExporting}
                            loadingText={language.adminProcesses.exportInProgress}
                            onClick={exportData}
                        >
                            {language.adminProcesses.exportProcessStep}
                        </Button>
                    </ButtonRow>
                </Card.Body>
            </Card>
        </Box>
    );

    function getDatasetTypeOption(value?: DatasetType) {
        for (const group of selectOptions) {
            for (const option of group.options) {
                if (option.value === value) {
                    return option;
                }
            }
        }
    }

    async function exportData() {
        const { hasErrors } = validate();

        if (hasErrors || !formData.datasetType) {
            return;
        }

        setIsExporting(true);

        const exporter = new Exporter(formData.datasetType, ExporterType.Excel);
        await exporter.download();

        setIsExporting(false);
    }
}
